/*
==============================================================================
SKeld Premium Product Reviews
Version: 1.0.0
Theme: Dawn 15.2+

assets/product-reviews.js

Eigene Slider Engine
CSV Loader
SVG Cache
Session Cache
Hotspots
Produktanimation
==============================================================================
*/

(() => {

'use strict';

/*==============================================================================
CONFIG
==============================================================================*/

const VERSION = '1.0.0';

const DEBUG_PREFIX = '[SKeld Reviews]';

const CACHE_PREFIX = 'skeld-reviews-';

const CACHE_TIME = 24 * 60 * 60 * 1000;

/*
------------------------------------------------------------------------------
CSV Dateien
------------------------------------------------------------------------------
*/

const CSV_FILES = {

    de: window.SKeldReviews.csv.de,

    en: window.SKeldReviews.csv.en,

    fr: window.SKeldReviews.csv.fr,

    it: window.SKeldReviews.csv.it,

    es: window.SKeldReviews.csv.es

};

/*
------------------------------------------------------------------------------
SVG Dateien
------------------------------------------------------------------------------
*/

const SVG_FILES = {

    star: window.SKeldReviews.svg.star,

    verified: window.SKeldReviews.svg.verified,

    quote: window.SKeldReviews.svg.quote,

    reply: window.SKeldReviews.svg.reply,

    arrowLeft: window.SKeldReviews.svg.arrowLeft,

    arrowRight: window.SKeldReviews.svg.arrowRight,

    close: window.SKeldReviews.svg.close

};

/*==============================================================================
MEMORY CACHE
==============================================================================*/

const MemoryCache = {

    csv: new Map(),

    svg: new Map(),

    templates: new Map()

};

/*==============================================================================
UTILS
==============================================================================*/

const Utils = {

    debug(section,...args){

        if(section.dataset.debug !== 'true') return;

        console.log(DEBUG_PREFIX,...args);

    },

    wait(ms){

        return new Promise(resolve=>{

            window.setTimeout(resolve,ms);

        });

    },

    clamp(value,min,max){

        return Math.min(max,Math.max(min,value));

    },

    locale(){

        let code=document.documentElement.lang;

        if(!code){

            code='en';

        }

        code=code.toLowerCase();

        if(code.includes('-')){

            code=code.split('-')[0];

        }

        return code;

    },

    csvName(){

        const lang=this.locale();

        if(CSV_FILES[lang]){

            return CSV_FILES[lang];

        }

        return CSV_FILES.en;

    },

    sessionKey(){

        return CACHE_PREFIX + this.csvName();

    },

    storageAvailable(){

        try{

            const test='__skeld__';

            sessionStorage.setItem(test,test);

            sessionStorage.removeItem(test);

            return true;

        }

        catch(e){

            return false;

        }

    }

};

/*==============================================================================
SESSION CACHE
==============================================================================*/

const SessionCache={

    get(key){

        if(!Utils.storageAvailable()){

            return null;

        }

        const raw=sessionStorage.getItem(key);

        if(!raw){

            return null;

        }

        try{

            const data=JSON.parse(raw);

            if(Date.now()-data.time>CACHE_TIME){

                sessionStorage.removeItem(key);

                return null;

            }

            return data.value;

        }

        catch(e){

            sessionStorage.removeItem(key);

            return null;

        }

    },

    set(key,value){

        if(!Utils.storageAvailable()){

            return;

        }

        sessionStorage.setItem(key,JSON.stringify({

            time:Date.now(),

            value:value

        }));

    },

    remove(key){

        if(!Utils.storageAvailable()){

            return;

        }

        sessionStorage.removeItem(key);

    }

};

/*==============================================================================
CSV LOADER
==============================================================================*/

const CSVLoader = {

    async load(section){

        const cacheKey = Utils.sessionKey();

        if(MemoryCache.csv.has(cacheKey)){

            Utils.debug(section,'CSV aus Memory Cache');

            return MemoryCache.csv.get(cacheKey);

        }

        const session = SessionCache.get(cacheKey);

        if(session){

            Utils.debug(section,'CSV aus Session Cache');

            MemoryCache.csv.set(cacheKey,session);

            return session;

        }

        let url = Utils.csvName();

        if(!url){

            console.error(DEBUG_PREFIX,'Keine CSV-Datei konfiguriert.');

            return [];

        }

        Utils.debug(section,'CSV wird geladen',url);

        let response = await fetch(url);

        if(!response.ok && url !== CSV_FILES.en){

            Utils.debug(section,'Fallback Englisch');

            response = await fetch(CSV_FILES.en);

        }

        if(!response.ok){

            console.error(DEBUG_PREFIX,'CSV konnte nicht geladen werden.');

            return [];

        }

        const text = await response.text();

        const parsed = CSVParser.parse(text);

        MemoryCache.csv.set(cacheKey,parsed);

        SessionCache.set(cacheKey,parsed);

        return parsed;

    }

};

/*==============================================================================
SVG CACHE
==============================================================================*/

const SVGCache = {

    async get(name){

        return this.load(name);

    }

};

/*==============================================================================
SECTION CLASS
==============================================================================*/

class ProductReviews{

    constructor(section){

        this.section=section;

        this.productId=section.dataset.productId;

        this.productHandle=section.dataset.productHandle;

        this.language=Utils.locale();

        this.reviews=[];

        this.filtered=[];

        this.settings = {

    showShopReply:
        section.dataset.showShopReply === 'true',

    showImages:
        section.dataset.showImages === 'true',

    showTitle:
        section.dataset.showTitle === 'true',

    showDate:
        section.dataset.showDate === 'true'

};

    }

}

/*==============================================================================
CSV PARSER
==============================================================================*/

const CSVParser = {

    parse(text){

        const rows = [];

        let row = [];

        let value = '';

        let insideQuotes = false;

        for(let i=0;i<text.length;i++){

            const c=text[i];

            if(c === '"'){

                if(insideQuotes && text[i+1] === '"'){

                    value += '"';

                    i++;

                    continue;

                }

                insideQuotes = !insideQuotes;

                continue;

            }

            if(c===',' && !insideQuotes){

                row.push(value);

                value='';

                continue;

            }

            if((c==='\n' || c==='\r') && !insideQuotes){

                if(c==='\r' && text[i+1]==='\n'){

                    i++;

                }

                row.push(value);

                value='';

                if(row.length){

                    rows.push(row);

                }

                row=[];

                continue;

            }

            value += c;

        }

        if(value.length || row.length){

            row.push(value);

            rows.push(row);

        }

        if(!rows.length){

            return [];

        }

        const header = rows.shift();

        return rows.map(line=>{

            const obj={};

            header.forEach((key,index)=>{

                obj[key.trim()] = line[index] ?? '';

            });

            return obj;

        });

    }

};


/*==============================================================================
CSV FILTER
==============================================================================*/

const ReviewFilter = {

    verified(reviews){

        return reviews.filter(review=>{

            return String(review.verified)

                .toLowerCase()

                .trim()==='true';

        });

    },


    product(reviews,productHandle,productId){

        let filtered = reviews.filter(review=>{

            return String(review.product_handle)

                .trim()===String(productHandle)

                .trim();

        });

        if(filtered.length){

            return filtered;

        }

        return reviews.filter(review=>{

            return String(review.product_id)

                .trim()===String(productId)

                .trim();

        });

    },


    favorite(reviews){

        return reviews.filter(review=>{

            return String(review.favorite)

                .trim()

                .toUpperCase()==='TRUE';

        });

    },


    sort(reviews){

        return reviews.sort((a,b)=>{

            return new Date(b.review_date)

                -

                new Date(a.review_date);

        });

    }

};




/*==============================================================================
SUMMARY
==============================================================================*/

/*const ReviewSummary = {

    average(reviews){

        if(!reviews.length){

            return 0;

        }

        let sum = 0;

        reviews.forEach(review=>{

            sum += Number(review.rating);

        });

        return Number(

            (sum/reviews.length)

            .toFixed(1)

        );

    },

    count(reviews){

        return reviews.length;

    }



};*/

const ReviewSummary = {

    average(reviews){

        if(!reviews.length){

            return 0;

        }

        let sum = 0;

        reviews.forEach(review=>{

            sum += Number(review.rating);

        });

        return sum / reviews.length;

    },

    count(reviews){

        return reviews.length;

    }

};


/*==============================================================================
REVIEW PREPARATION
==============================================================================*/

ProductReviews.prototype.prepare = function(){

    /*
    ============================================================
    NUR VERIFIZIERTE REVIEWS
    ============================================================
    */

    let reviews = ReviewFilter.verified(

        this.reviews

    );


    /*
    ============================================================
    PRODUKTSEITE
    ============================================================
    */

    if(this.productId || this.productHandle){

        /*
        --------------------------------------------------------
        Zuerst normale Produktreviews suchen
        --------------------------------------------------------
        */

        const productReviews = ReviewFilter.product(

            reviews,

            this.productHandle,

            this.productId

        );


        /*
        --------------------------------------------------------
        PRODUKTREVIEWS VORHANDEN
        --------------------------------------------------------
        */

        if(productReviews.length){

            reviews = productReviews;

        }


        /*
        --------------------------------------------------------
        KEINE PRODUKTREVIEWS
        → FAVORITE REVIEWS ALS FALLBACK
        --------------------------------------------------------
        */

        else{

            reviews = ReviewFilter.favorite(

                reviews

            );

            Utils.debug(

                this.section,

                'Keine Produktreviews gefunden – Favorite-Fallback:',

                reviews.length

            );

        }

    }


    /*
    ============================================================
    NORMALE SEITE / LANDINGPAGE
    ============================================================
    */

    else{

        reviews = ReviewFilter.favorite(

            reviews

        );

    }


    /*
    ============================================================
    SORTIERUNG
    ============================================================
    */

    reviews = ReviewFilter.sort(

        reviews

    );


    /*
    ============================================================
    ERGEBNIS
    ============================================================
    */

    this.filtered = reviews;


    Utils.debug(

        this.section,

        'Gefilterte Reviews:',

        reviews.length

    );

};
/*==============================================================================
SVG LOADER
==============================================================================*/

SVGCache.load = async function(name){

    if(MemoryCache.svg.has(name)){

        return MemoryCache.svg.get(name).cloneNode(true);

    }

    const url = SVG_FILES[name];

    if(!url){

        return document.createTextNode('');

    }

    try{

        const response = await fetch(url);

        if(!response.ok){

            throw new Error(name);

        }

        const svgText = await response.text();

        const wrapper = document.createElement('div');

        wrapper.innerHTML = svgText.trim();

        const svg = wrapper.querySelector('svg');

        if(!svg){

            throw new Error(name);

        }
        /* ---------------------------------------
SVG normalisieren
--------------------------------------- */

svg.removeAttribute('width');
svg.removeAttribute('height');

if (!svg.hasAttribute('viewBox')) {

    const w = svg.getAttribute('width') || 24;
    const h = svg.getAttribute('height') || 24;

    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
}

svg.setAttribute('width', '100%');
svg.setAttribute('height', '100%');
svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

        MemoryCache.svg.set(name,svg);

        return svg.cloneNode(true);

    }

    catch(error){

        console.warn(DEBUG_PREFIX,'SVG konnte nicht geladen werden:',name);

        return document.createTextNode('');

    }

};


/*==============================================================================
SVG REPLACE
==============================================================================*/

ProductReviews.prototype.replaceSVG = async function(root){

    const holders = root.querySelectorAll('.pr-svg');

    for(const holder of holders){

        const svgClass = [...holder.classList]
            .find(name => name.startsWith('pr-svg-'));

        if(!svgClass){
            continue;
        }

        const key = svgClass
            .replace('pr-svg-','')
            .replace('arrow-left','arrowLeft')
            .replace('arrow-right','arrowRight');

        const icon = await SVGCache.load(key);

        holder.replaceChildren(icon);

    }

};


/*==============================================================================
SUMMARY
==============================================================================*/

ProductReviews.prototype.renderSummary = function(){

    const average =
        ReviewSummary.average(this.reviews);


    const count =
        ReviewSummary.count(this.reviews);

       const stars =
    this.section.querySelector(
        '[data-rating-stars]'
    );

if(stars){

    stars.innerHTML =
        '<span class="pr-svg pr-svg-star"></span>'.repeat(5);

    this.replaceSVG(stars);

}

    const rating =
        this.section.querySelector(
            '[data-rating-average]'
        );

    const total =
        this.section.querySelector(
            '[data-rating-count]'
        );

    if(rating){

        rating.textContent =
            average.toFixed(2);


    }

    if(total){

     total.textContent =
    `(${count} ${window.themeStrings.productReviews.reviews})`;

    }

};


/*==============================================================================
REVIEW CARD
==============================================================================*/

ProductReviews.prototype.createCard=

async function(review){

    const template=

        document.getElementById(

            'pr-review-template'

        );

    const card=

        template.content

        .firstElementChild

        .cloneNode(true);

    card.querySelector(

        '[data-review-title]'

    ).textContent=

        review.title;

    card.querySelector(

        '[data-review-text]'

    ).textContent=

        review.review;

    card.querySelector(

        '[data-review-author]'

    ).textContent=

        review.reviewer_name;

    card.querySelector(

        '[data-review-date]'

    ).textContent=

        review.review_date;

    /*
    --------------------------------------
    Sterne
    --------------------------------------
    */

    const stars=

        card.querySelector(

            '[data-review-stars]'

        );

    stars.innerHTML='';

    for(

        let i=0;

        i<Number(review.rating);

        i++

    ){

        const svg=

            await SVGCache.load(

                'star'

            );

        stars.append(svg);

    }

    /*
    --------------------------------------
    Bilder
    --------------------------------------
    */

    if(review.picture_urls){

        const gallery=

            card.querySelector(

                '[data-review-gallery]'

            );

        const template=

            document.getElementById(

                'pr-image-template'

            );

        review.picture_urls

            .split(',')

            .map(url=>url.trim())

            .filter(Boolean)

            .forEach(url=>{

                const item=

                    template.content

                    .firstElementChild

                    .cloneNode(true);

                const image=

                    item.querySelector('img');

                image.src=url;

                image.alt=review.title;

                gallery.append(item);

            });

    }

    await this.replaceSVG(card);

this.initReadMore(card);

return card;

};


/*==============================================================================
SHOP REPLY
==============================================================================*/

ProductReviews.prototype.createReply=

async function(review){

    if(
    !this.settings.showShopReply ||
    !review.shop_reply
){

    return null;

}

    const template=

        document.getElementById(

            'pr-reply-template'

        );

    const card=

        template.content

        .firstElementChild

        .cloneNode(true);

    card.querySelector(

        '[data-reply-text]'

    ).textContent=

        review.shop_reply;

    await this.replaceSVG(card);

    return card;

};

/*==============================================================================
READ MORE
==============================================================================*/

ProductReviews.prototype.initReadMore = function(card){

    const text =
        card.querySelector('[data-review-text]');

    const button =
        card.querySelector('[data-read-more]');

    if(!text || !button){

        return;

    }

    requestAnimationFrame(()=>{

        // Prüfen, ob der Text tatsächlich abgeschnitten wurde
        if(text.scrollHeight <= text.clientHeight + 2){

            button.remove();

            return;

        }

        button.hidden = false;

        button.addEventListener('click', () => {


    const expanded =
        card.classList.toggle('is-expanded');

    button.textContent = expanded
        ? window.themeStrings.productReviews.readLess
        : window.themeStrings.productReviews.readMore;

    this.slider.equalHeight();

});

    });

};
/*==============================================================================
RENDER REVIEWS
==============================================================================*/

ProductReviews.prototype.renderReviews = async function(){

    const container =
        this.section.querySelector(
            '[data-review-track]'
        );

    if(!container){

        return;

    }

    container.innerHTML='';

    for (const review of this.filtered) {

    const reviewCard = await this.createCard(review);

    container.appendChild(reviewCard);

    const replyCard = await this.createReply(review);

    if (replyCard) {

        reviewCard.appendChild(replyCard);

    }

}

};


/*==============================================================================
STAR FILTER
==============================================================================*/

ProductReviews.prototype.bindFilter=function(){

    const buttons =
    this.section.querySelectorAll(
        '.pr-filter-button'
    );

    if(!buttons.length){

        return;

    }


    buttons.forEach(button=>{

        button.addEventListener('click',()=>{

            buttons.forEach(item=>{

                item.classList.remove('is-active');

            });

            button.classList.add('is-active');


            const rating = button.dataset.rating;


            /*
            ============================================================
            BASIS-FILTER
            ============================================================
            */

            let reviews = ReviewFilter.verified(

                this.reviews

            );


            /*
            ============================================================
            PRODUKTSEITE
            ============================================================
            */

            if(this.productId || this.productHandle){

                reviews = ReviewFilter.product(

                    reviews,

                    this.productHandle,

                    this.productId

                );

            }


            /*
            ============================================================
            NORMALE SEITE / FAVORITEN
            ============================================================
            */

            else{

                reviews = ReviewFilter.favorite(

                    reviews

                );

            }


            /*
            ============================================================
            STERNEFILTER
            ============================================================
            */

            if(rating !== 'all'){

                reviews = reviews.filter(review=>{

                    return Number(review.rating)

                        ===

                        Number(rating);

                });

            }


            reviews = ReviewFilter.sort(

                reviews

            );


            this.filtered = reviews;


            this.renderSummary();


            this.renderReviews()

                .then(()=>{

                    if(this.slider){

                        this.slider.refresh();

                    }

                });

        });

    });

};


/*==============================================================================
LIGHTBOX
==============================================================================*/

ProductReviews.prototype.bindLightbox=function(){

    const lightbox=

        this.section.querySelector(

            '.pr-lightbox'

        );

    if(!lightbox){

        return;

    }

    this.section

        .addEventListener(

            'click',

            event=>{

                const image=

                    event.target.closest(

                        '.pr-gallery-item img'

                    );

                if(!image){

                    return;

                }

                lightbox.hidden = false;

lightbox.setAttribute(

    'aria-hidden',

    'false'

);

lightbox

    .querySelector(

        '.pr-lightbox-image'

    )

    .src = image.src;

lightbox

    .classList

    .add(

        'is-open'

    );

            }

        );

    lightbox

        .querySelector(

            '.pr-lightbox-overlay'

        )

        .addEventListener(

            'click',

            ()=>{

                lightbox.classList.remove(

    'is-open'

);

lightbox.hidden = true;

lightbox.setAttribute(

    'aria-hidden',

    'true'

);

            }

        );

    lightbox

        .querySelector(

            '.pr-lightbox-close'

        )

        .addEventListener(

            'click',

            ()=>{

                lightbox.classList.remove(

    'is-open'

);

lightbox.hidden = true;

lightbox.setAttribute(

    'aria-hidden',

    'true'

);

            }

        );

    document

        .addEventListener(

            'keydown',

            event=>{

                if(event.key==='Escape'){

                    lightbox.classList.remove(

    'is-open'

);

lightbox.hidden = true;

lightbox.setAttribute(

    'aria-hidden',

    'true'

);

                }

            }

        );

};


/*==============================================================================
SLIDER ENGINE
==============================================================================*/

class ReviewSlider{

    constructor(app){

        this.app=app;

        this.section=app.section;

        this.viewport=
            this.section.querySelector(
                '[data-slider-viewport]'
            );

        this.track=
            this.section.querySelector(
                '[data-review-track]'
            );

        this.prev=
            this.section.querySelector(
                '[data-slider-prev]'
            );

        this.next=
            this.section.querySelector(
                '[data-slider-next]'
            );

        this.index=0;

        this.cardWidth=0;

        this.cards=[];

        this.dragging=false;

        this.pointerId=null;

        this.startX=0;

        this.currentX=0;

        this.deltaX=0;

        this.currentTranslate=0;

        this.autoplay=null;

        this.resizeObserver=null;

        this.hover=false;

        this.touching=false;

        this.visibleCards=1;

    }

refresh(){

    this.cards =
        [...this.track.children];

    console.log(
        'Slider Cards:',
        this.cards.length
    );

    if(!this.cards.length){

        return;

    }

    this.updateVisibleCards();

    requestAnimationFrame(()=>{

        const gap =
            parseFloat(
                getComputedStyle(
                    this.track
                ).gap
            ) || 0;

        this.cardWidth =
            this.cards[0].offsetWidth + gap;

        console.log(
            'Card width:',
            this.cardWidth
        );

        this.goTo(
            this.index,
            false
        );

    });

}
updateVisibleCards(){

    const noProductImage =
        this.section.classList.contains(
            'pr-section--no-product-image'
        );

    if(
        noProductImage &&
        window.innerWidth >= 750
    ){

        this.visibleCards=2;

    }else{

        this.visibleCards=1;

    }

}

 updateLayout(){

    if(!this.cards.length){

        return;

    }

    this.updateVisibleCards();


    /*
    ==========================================
    AKTUELLEN INDEX BEGRENZEN
    ==========================================
    */

    const maxIndex =

        Math.max(

            0,

            this.cards.length -

            this.visibleCards

        );

    if(this.index > maxIndex){

        this.index = maxIndex;

    }


    /*
    ==========================================
    KARTENBREITE NEU BERECHNEN
    ==========================================
    */

    const gap =

        parseFloat(

            getComputedStyle(

                this.track

            ).gap

        ) || 0;


    this.cardWidth =

        this.cards[0].offsetWidth + gap;


    /*
    ==========================================
    SLIDER POSITION AKTUALISIEREN
    ==========================================
    */

    this.goTo(

        this.index,

        false

    );


    requestAnimationFrame(()=>{

        this.updateViewportHeight();

    });

}


updateViewportHeight(){

    if(!this.viewport){

        return;

    }

    if(!this.cards.length){

        return;

    }

    /*
    ==========================================
    HÖHE DER SICHTBAREN KARTEN ERMITTELN
    ==========================================
    */

    const visibleCards =

        this.cards.slice(

            this.index,

            this.index + this.visibleCards

        );


    if(!visibleCards.length){

        return;

    }


    const maxHeight =

        Math.max(

            ...visibleCards.map(

                card => card.offsetHeight

            )

        );


    /*
    ==========================================
    VIEWPORT AUF HÖCHSTE SICHTBARE KARTE SETZEN
    ==========================================
    */

    this.viewport.style.height =

        (maxHeight + 2) + 'px';

}
/*==============================================================================
GO TO
==============================================================================*/

goTo(index,animate=true){

    if(!this.cards.length){

        return;

    }

    /*
    ==========================================
    ZWEI KARTEN AUF DESKTOP BERÜCKSICHTIGEN
    ==========================================
    */

    const maxIndex =

        Math.max(

            0,

            this.cards.length -

            this.visibleCards

        );


    if(index<0){

        index=maxIndex;

    }

    if(index>maxIndex){

        index=0;

    }


    this.index=index;


    this.currentTranslate=

        -(

            this.cardWidth *

            index

        );


    /*
    ==========================================
    TRANSITION
    ==========================================
    */

    if(animate){

        const duration =

            getComputedStyle(

                this.section

            )

            .getPropertyValue(

                '--pr-slider-duration'

            )

            .trim()

            || '450ms';


        this.track.style.transition=

            `transform ${duration} cubic-bezier(.22,.61,.36,1)`;

    }else{

        this.track.style.transition=

            'none';

    }


    /*
    ==========================================
    TRACK VERSCHIEBEN
    ==========================================
    */

    this.track.style.transform=

        `translate3d(

            ${this.currentTranslate}px,

            0,

            0

        )`;


    requestAnimationFrame(()=>{

        this.updateViewportHeight();

    });

}


nextSlide(){

    this.goTo(

        this.index+1

    );

}


prevSlide(){

    this.goTo(

        this.index-1

    );

}
    /*==========================================================================
    DRAG
    ==========================================================================*/

   pointerDown(event){

    if(!this.cards.length){
        return;
    }

    // Buttons und Links dürfen normal geklickt werden
    const interactive = event.target.closest(
    'button,a,input,textarea,select,.pr-read-more'
);

if(interactive){
    return;
}

    this.dragging = true;

    this.pointerId = event.pointerId;

    this.startX = event.clientX;

    this.deltaX = 0;

    this.track.style.transition = 'none';

    this.viewport.setPointerCapture(event.pointerId);

}

    pointerMove(event){

        if(!this.dragging){

            return;

        }

        this.currentX=event.clientX;

        this.deltaX=this.currentX-this.startX;

        this.track.style.transform=

            `translate3d(${this.currentTranslate+this.deltaX}px,0,0)`;

    }

    pointerUp(){

        if(!this.dragging){

            return;

        }

        this.dragging=false;

        const threshold=this.cardWidth*0.18;

        if(this.deltaX<-threshold){

            this.nextSlide();

        }

        else if(this.deltaX>threshold){

            this.prevSlide();

        }

        else{

            this.goTo(this.index);

        }

    }


    /*==========================================================================
    AUTOPLAY
    ==========================================================================*/

    startAutoplay(){

        this.stopAutoplay();

      const enabled =
    this.section.dataset.autoplay === 'true';

if (!enabled) {

    return;

}

const speed =
    Number(this.section.dataset.autoplaySpeed) || 5000;

this.autoplay = window.setInterval(() => {

    if (this.hover) return;

    if (this.dragging) return;

    if (this.touching) return;

    if (document.hidden) return;

    this.nextSlide();

}, speed);

    }

    stopAutoplay(){

        if(this.autoplay){

            clearInterval(this.autoplay);

            this.autoplay=null;

        }

    }


    /*==========================================================================
    EVENTS
    ==========================================================================*/

    bindEvents(){

        this.viewport.addEventListener(

            'pointerdown',

            this.pointerDown.bind(this),

            {

                passive:true

            }

        );

        window.addEventListener(

            'pointermove',

            this.pointerMove.bind(this),

            {

                passive:true

            }

        );

        window.addEventListener(

            'pointerup',

            this.pointerUp.bind(this),

            {

                passive:true

            }

        );

        this.viewport.addEventListener(

            'mouseenter',

            ()=>{

                this.hover=true;

            }

        );

        this.viewport.addEventListener(

            'mouseleave',

            ()=>{

                this.hover=false;

            }

        );

        this.viewport.addEventListener(

            'touchstart',

            ()=>{

                this.touching=true;

            },

            {

                passive:true

            }

        );

        this.viewport.addEventListener(

            'touchend',

            ()=>{

                this.touching=false;

            },

            {

                passive:true

            }

        );

        if(this.prev){

            this.prev.addEventListener(

                'click',

                ()=>{

                    this.prevSlide();

                }

            );

        }

        if(this.next){

            this.next.addEventListener(

                'click',

                ()=>{

                    this.nextSlide();

                }

            );

        }

        document.addEventListener(

            'keydown',

            event=>{

                if(event.key==='ArrowLeft'){

                    this.prevSlide();

                }

                if(event.key==='ArrowRight'){

                    this.nextSlide();

                }

            }

        );

    }


    /*==========================================================================
    RESIZE
    ==========================================================================*/

   observeResize(){

    let resizeTimeout = null;

    window.addEventListener('resize', () => {

        clearTimeout(resizeTimeout);

        resizeTimeout = setTimeout(() => {

            this.updateLayout();

        }, 120);

    });

}


    /*==========================================================================
    INIT
    ==========================================================================*/

    init(){

        this.refresh();

        this.bindEvents();

        this.observeResize();

        this.startAutoplay();

    }

}

/*==============================================================================
SLIDER START
==============================================================================*/

ProductReviews.prototype.createSlider=function(){

    this.slider=

        new ReviewSlider(this);

    this.slider.init();

};


/*==============================================================================
HOTSPOTS
==============================================================================*/

class HotspotEngine{

    constructor(app){

        this.app = app;

        this.section = app.section;

        this.hotspots = [

            ...this.section.querySelectorAll('[data-hotspot]')

        ];

        this.current = null;

        this.timer = null;

        this.duration = 5000;

    }


    /*----------------------------------------------------------------------
    HOTSPOT ANZEIGEN
    ----------------------------------------------------------------------*/

    show(index){

        if(!this.hotspots.length){

            return;

        }

        this.hotspots.forEach(hotspot=>{

            hotspot.classList.remove(

                'is-active'

            );

        });

        const hotspot = this.hotspots[index];

        if(!hotspot){

            return;

        }

        this.current = index;

        hotspot.classList.add(

            'is-active'

        );

        this.startAutoClose();

    }


    /*----------------------------------------------------------------------
    POPUP AUTOMATISCH SCHLIESSEN
    ----------------------------------------------------------------------*/

    startAutoClose(){

        this.stopAutoClose();

        this.timer = window.setTimeout(()=>{

            this.close();

        },this.duration);

    }


    /*----------------------------------------------------------------------
    POPUP SCHLIESSEN
    ----------------------------------------------------------------------*/

    close(){

        this.hotspots.forEach(hotspot=>{

            hotspot.classList.remove(

                'is-active'

            );

        });

        this.current = null;

        this.stopAutoClose();

    }


    /*----------------------------------------------------------------------
    TIMER STOPPEN
    ----------------------------------------------------------------------*/

    stopAutoClose(){

        if(this.timer){

            clearTimeout(

                this.timer

            );

            this.timer = null;

        }

    }


    /*----------------------------------------------------------------------
    EVENTS
    ----------------------------------------------------------------------*/

    bind(){

        this.hotspots.forEach((hotspot,index)=>{

            const button = hotspot.querySelector(

                '.pr-hotspot-button'

            );

            if(!button){

                return;

            }

            button.addEventListener(

                'click',

                event=>{

                    event.preventDefault();

                    event.stopPropagation();

                    if(this.current === index){

                        this.close();

                    }else{

                        this.show(index);

                    }

                }

            );

        });


        /* Popup schließen, wenn außerhalb geklickt wird */

        document.addEventListener(

            'click',

            event=>{

                if(!event.target.closest(

                    `[data-section-id="${this.section.dataset.sectionId}"] [data-hotspot]`

                )){

                    this.close();

                }

            }

        );

    }


    /*----------------------------------------------------------------------
    INITIALISIERUNG
    ----------------------------------------------------------------------*/

    init(){

        if(!this.hotspots.length){

            return;

        }

        /*

        WICHTIG:

        Kein show(0)

        Kein start()

        Keine automatische Popup-Animation

        Alle Hotspots starten geschlossen.

        */

        this.close();

        this.bind();

    }


    /*----------------------------------------------------------------------
    CLEANUP
    ----------------------------------------------------------------------*/

    stop(){

        this.stopAutoClose();

        this.close();

    }

}


/*==============================================================================
PRODUCT FLOAT
==============================================================================*/

class ProductAnimation{

    constructor(app){

        this.app = app;

        this.image = app.section.querySelector(

            '[data-product-image]'

        );

        this.shadow = app.section.querySelector(

            '[data-product-shadow]'

        );

        this.time = 0;

        this.frame = null;

    }

    animate = ()=>{

        this.time += 0.02;

        const y =

            Math.sin(this.time)

            * 6;

        const rotate =

            Math.sin(this.time * .7)

            * 1.5;

        if(this.image){

            this.image.style.transform=

                `translateY(${y}px) rotate(${rotate}deg)`;

        }

        if(this.shadow){

            const scale=

                1-(Math.abs(y)/45);

            this.shadow.style.transform=

                `scale(${scale})`;

            this.shadow.style.opacity=

                0.22+(Math.abs(y)/40);

        }

        this.frame=

            requestAnimationFrame(

                this.animate

            );

    }

    init(){

        if(!this.image){

            return;

        }

        this.animate();

    }

}


/*==============================================================================
START ENGINES
==============================================================================*/

ProductReviews.prototype.startEngines=function(){

    this.slider=

        new ReviewSlider(this);

    this.slider.init();

    this.hotspots=

        new HotspotEngine(this);

    this.hotspots.init();

    this.productAnimation=

        new ProductAnimation(this);

    this.productAnimation.init();

};


/*==============================================================================
INTERSECTION OBSERVER
==============================================================================*/

class VisibilityObserver{

    constructor(app){

        this.app = app;

        this.section = app.section;

        this.slider = app.slider;

        this.observer = null;

    }

    init(){

        this.observer = new IntersectionObserver(entries=>{

            entries.forEach(entry=>{

                if(!this.slider){

                    return;

                }

                if(entry.isIntersecting){

                    this.slider.startAutoplay();

                }else{

                    this.slider.stopAutoplay();

                }

            });

        },{

            threshold:0.35

        });

        this.observer.observe(this.section);

    }

}


/*==============================================================================
LAZY IMAGES
==============================================================================*/

ProductReviews.prototype.lazyImages=function(){

    const images=this.section.querySelectorAll(

        '.pr-gallery-item img'

    );

    images.forEach(image=>{

        image.loading='lazy';

        image.decoding='async';

        image.fetchPriority='low';

    });

};


/*==============================================================================
ACCESSIBILITY
==============================================================================*/

ProductReviews.prototype.accessibility=function(){

    this.section.setAttribute(

        'role',

        'region'

    );

    this.section.setAttribute(

        'aria-label',

        'Product Reviews'

    );

    const viewport=

        this.section.querySelector(

            '[data-slider-viewport]'

        );

    if(viewport){

        viewport.tabIndex=0;

    }

};


/*==============================================================================
MATCH HEIGHT
==============================================================================*/

ProductReviews.prototype.matchCardHeight=function(){

    if(!this.slider){

        return;

    }

    requestAnimationFrame(()=>{

        this.slider.equalHeight();

    });

};


/*==============================================================================
MEMORY CLEANUP
==============================================================================*/

ProductReviews.prototype.destroy=function(){

    if(this.slider){

        this.slider.stopAutoplay();

        if(this.slider.resizeObserver){

            this.slider.resizeObserver.disconnect();

        }

    }

    if(this.hotspots){

        this.hotspots.stop();

    }

    if(this.productAnimation){

        cancelAnimationFrame(

            this.productAnimation.frame

        );

    }

    if(this.visibilityObserver){

        this.visibilityObserver.observer.disconnect();

    }

};


/*==============================================================================
PAGE EVENTS
==============================================================================*/

ProductReviews.prototype.bindPageEvents=function(){

    document.addEventListener(

        'visibilitychange',

        ()=>{

            if(!this.slider){

                return;

            }

            if(document.hidden){

                this.slider.stopAutoplay();

            }else{

                this.slider.startAutoplay();

            }

        }

    );

    window.addEventListener(

        'pagehide',

        ()=>{

            this.destroy();

        }

    );

};


/*==============================================================================
START UPDATE
==============================================================================*/

ProductReviews.prototype.start=async function(){

    this.prepare();

    this.renderSummary();

    await this.renderReviews();

    this.bindFilter();

    this.bindLightbox();

    this.startEngines();

    await this.replaceSVG(this.section);

    this.lazyImages();

    this.accessibility();

    this.matchCardHeight();

    this.visibilityObserver=

        new VisibilityObserver(this);

    this.visibilityObserver.init();

    this.bindPageEvents();

};
/*==============================================================================
DEBUG
==============================================================================*/

ProductReviews.prototype.debugInfo=function(){

    if(this.section.dataset.debug!=='true'){

        return;

    }

    console.groupCollapsed(

        DEBUG_PREFIX

    );

    console.table({

        version:VERSION,

        language:this.language,

        productHandle:this.productHandle,

        productId:this.productId,

        reviewsLoaded:this.reviews.length,

        reviewsFiltered:this.filtered.length,

        average:

            ReviewSummary.average(

                this.filtered

            ),

        cache:

            Utils.sessionKey()

    });

    console.groupEnd();

};


/*==============================================================================
ERROR HANDLER
==============================================================================*/

window.addEventListener(

    'error',

    event=>{

        console.error(

            DEBUG_PREFIX,

            event.message

        );

    }

);

window.addEventListener(

    'unhandledrejection',

    event=>{

        console.error(

            DEBUG_PREFIX,

            event.reason

        );

    }

);


/*==============================================================================
PERFORMANCE
==============================================================================*/

const Performance = {

    preload(){

        Object.values(SVG_FILES)

            .filter(Boolean)

            .forEach(url=>{

                fetch(url,{

                    cache:'force-cache'

                }).catch(()=>{});

            });

    }

};


/*==============================================================================
FINAL START
==============================================================================*/

ProductReviews.prototype.init=async function(){

    try{

        Utils.debug(

            this.section,

            'Initialisierung'

        );

        Performance.preload();

        this.reviews=

            await CSVLoader.load(

                this.section

            );

        await this.start();

        this.debugInfo();

    }

    catch(error){

        console.error(

            DEBUG_PREFIX,

            error

        );

    }

};


/*==============================================================================
AUTO INIT
==============================================================================*/

const Applications=[];

function boot(){

    document

        .querySelectorAll(

            '.pr-section'

        )

        .forEach(section=>{

            const app=

                new ProductReviews(

                    section

                );

            Applications.push(app);

            app.init();

        });

}

if(document.readyState==='loading'){

    document.addEventListener(

        'DOMContentLoaded',

        boot,

        {

            once:true,

            passive:true

        }

    );

}

else{

    boot();

}

/*==============================================================================
END
==============================================================================*/

})();
